/**
 * ReviewAuditLog — Activity timeline for a review session.
 *
 * Shows a chronological list of all actions taken on a review:
 * finding decisions, collaborator changes, session lifecycle events.
 */

import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import LogoSpinner from '../shared/LogoSpinner';
import {
  PiCheckCircleLight,
  PiXCircleLight,
  PiPencilSimpleLight,
  PiArrowUpRightLight,
  PiUsersLight,
  PiPlayLight,
  PiTrashLight,
  PiClockLight,
  PiArrowCounterClockwiseLight,
  PiSealCheckLight,
  PiFlagCheckeredLight,
} from 'react-icons/pi';

import { fetchAuditLog } from '../../store/actions/Review/ReviewActions';
import { showAuditLogDetailModal } from '../../store/actions/modalActions';
import { AuditLogEntry as GenericAuditLogEntry } from '../../store/actions/AuditLogActions';
import { AuditLogEntry } from '../../types/review';

/** Convert snake_case or slug text into Title Case for display */
const formatLabel = (text: string): string =>
  text
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

const actionIcons: Record<string, React.ReactNode> = {
  finding_accepted: <PiCheckCircleLight className="w-3.5 h-3.5 text-emerald-500" />,
  finding_rejected: <PiXCircleLight className="w-3.5 h-3.5 text-red-500" />,
  finding_modified: <PiPencilSimpleLight className="w-3.5 h-3.5 text-amber-500" />,
  finding_escalated: <PiArrowUpRightLight className="w-3.5 h-3.5 text-purple-500" />,
  finding_decision_undone: <PiArrowCounterClockwiseLight className="w-3.5 h-3.5 text-gray-400" />,
  bulk_finding_accepted: <PiCheckCircleLight className="w-3.5 h-3.5 text-emerald-500" />,
  bulk_finding_rejected: <PiXCircleLight className="w-3.5 h-3.5 text-red-500" />,
  collaborator_added: <PiUsersLight className="w-3.5 h-3.5 text-blue-500" />,
  collaborator_updated: <PiUsersLight className="w-3.5 h-3.5 text-blue-500" />,
  collaborator_removed: <PiUsersLight className="w-3.5 h-3.5 text-gray-400" />,
  review_started: <PiPlayLight className="w-3.5 h-3.5 text-emerald-500" />,
  review_deleted: <PiTrashLight className="w-3.5 h-3.5 text-red-500" />,
  review_renamed: <PiPencilSimpleLight className="w-3.5 h-3.5 text-gray-400" />,
  status_change: <PiFlagCheckeredLight className="w-3.5 h-3.5 text-blue-500" />,
  sign_off: <PiSealCheckLight className="w-3.5 h-3.5 text-emerald-500" />,
  review_finalized: <PiSealCheckLight className="w-3.5 h-3.5 text-emerald-600" />,
  review_reopened: <PiArrowCounterClockwiseLight className="w-3.5 h-3.5 text-amber-500" />,
};

/** Dot color for the timeline connector */
const dotColors: Record<string, string> = {
  finding_accepted: 'bg-emerald-400',
  finding_rejected: 'bg-red-400',
  finding_modified: 'bg-amber-400',
  finding_escalated: 'bg-purple-400',
  finding_decision_undone: 'bg-gray-300 dark:bg-gray-600',
  bulk_finding_accepted: 'bg-emerald-400',
  bulk_finding_rejected: 'bg-red-400',
  review_started: 'bg-emerald-400',
  review_finalized: 'bg-emerald-500',
  sign_off: 'bg-emerald-400',
  status_change: 'bg-blue-400',
  review_reopened: 'bg-amber-400',
};

interface ReviewAuditLogProps {
  sessionUuid: string;
  /** When provided, finding-related entries become clickable. */
  onFindingSelect?: (findingUuid: string) => void;
}

const FINDING_ACTIONS = new Set([
  'finding_accepted',
  'finding_rejected',
  'finding_modified',
  'finding_escalated',
  'finding_decision_undone',
]);

const ReviewAuditLog: React.FC<ReviewAuditLogProps> = ({ sessionUuid, onFindingSelect }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const auditLog = useSelector((s: RootState) => s.review.auditLog);
  const isLoading = useSelector((s: RootState) => s.review.isLoadingAuditLog);
  const total = useSelector((s: RootState) => s.review.auditLogTotal);

  /** Build clean, human-readable details for the detail modal.
   *  Strips internal UUIDs, nulls, and formats values nicely. */
  const buildReadableDetails = (entry: AuditLogEntry): Record<string, any> | null => {
    const raw = entry.details;
    if (!raw || Object.keys(raw).length === 0) return null;

    const result: Record<string, any> = {};

    // Map of raw keys → human-readable keys + value transforms
    const fieldMap: Record<string, { label: string; transform?: (v: any) => any }> = {
      name: { label: 'Review Name' },
      document_count: { label: 'Documents', transform: (v) => `${v} document${v !== 1 ? 's' : ''}` },
      clause_type: { label: 'Clause Type', transform: (v) => formatLabel(String(v)) },
      severity: { label: 'Severity', transform: (v) => formatLabel(String(v)) },
      decision: { label: 'Decision', transform: (v) => formatLabel(String(v)) },
      previous_decision: { label: 'Previous Decision', transform: (v) => formatLabel(String(v)) },
      notes: { label: 'Notes' },
      modified_text: { label: 'Modified Text' },
      count: { label: 'Findings Count' },
      access: { label: 'Access Level', transform: (v) => formatLabel(String(v)) },
      old_access: { label: 'Previous Access', transform: (v) => formatLabel(String(v)) },
      new_access: { label: 'New Access', transform: (v) => formatLabel(String(v)) },
      new_name: { label: 'New Name' },
      to: { label: 'New Status', transform: (v) => formatLabel(String(v)) },
      from: { label: 'Previous Status', transform: (v) => formatLabel(String(v)) },
      finding_uuids: { label: 'Findings Affected', transform: (v) => Array.isArray(v) ? `${v.length} finding${v.length !== 1 ? 's' : ''}` : v },
    };

    // Skip internal/UUID fields
    const skipFields = new Set(['playbook_uuid', 'parent_review_uuid', 'entity_uuid', 'finding_uuid', 'user_uuid', 'session_uuid']);

    for (const [key, value] of Object.entries(raw)) {
      if (value === null || value === undefined) continue;
      if (skipFields.has(key)) continue;
      if (typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) continue;

      const mapping = fieldMap[key];
      if (mapping) {
        result[mapping.label] = mapping.transform ? mapping.transform(value) : value;
      } else {
        result[formatLabel(key)] = value;
      }
    }

    return Object.keys(result).length > 0 ? result : null;
  };

  const mapToGenericEntry = (entry: AuditLogEntry): GenericAuditLogEntry => {
    const readableDetails = buildReadableDetails(entry);
    return {
      id: entry.id,
      entity_type: entry.entity_type || 'review_session',
      entity_uuid: entry.entity_uuid || sessionUuid,
      action: entry.action,
      user_name: entry.user_name,
      details: null,
      details_json: readableDetails,
      before_json: null,
      after_json: null,
      created_at: entry.created_at || '',
    };
  };

  useEffect(() => {
    if (sessionUuid) {
      dispatch(fetchAuditLog(sessionUuid, { page: 1, page_size: 200 }) as any);
    }
  }, [dispatch, sessionUuid]);

  const formatTime = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
    if (isNaN(date.getTime())) return dateStr;
    const now = Date.now();
    const diff = now - date.getTime();

    if (diff < 60000) return t('just now');
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  /** Build a concise, human-readable description */
  const getActionDescription = (entry: AuditLogEntry): React.ReactNode => {
    const details = entry.details || {};
    const userName = entry.user_name || t('System');
    const nameEl = <span className="font-medium text-gray-800 dark:text-gray-200">{userName}</span>;

    switch (entry.action) {
      case 'finding_accepted':
        return <>{nameEl} accepted <span className="text-gray-500 dark:text-gray-400">{details.clause_type ? formatLabel(details.clause_type) : 'a finding'}</span></>;
      case 'finding_rejected':
        return <>{nameEl} rejected <span className="text-gray-500 dark:text-gray-400">{details.clause_type ? formatLabel(details.clause_type) : 'a finding'}</span></>;
      case 'finding_modified':
        return <>{nameEl} modified <span className="text-gray-500 dark:text-gray-400">{details.clause_type ? formatLabel(details.clause_type) : 'a finding'}</span></>;
      case 'finding_escalated':
        return <>{nameEl} escalated <span className="text-gray-500 dark:text-gray-400">{details.clause_type ? formatLabel(details.clause_type) : 'a finding'}</span></>;
      case 'finding_decision_undone':
        return <>{nameEl} undid decision on <span className="text-gray-500 dark:text-gray-400">{details.clause_type ? formatLabel(details.clause_type) : 'a finding'}</span></>;
      case 'bulk_finding_accepted':
        return <>{nameEl} accepted <span className="text-gray-500 dark:text-gray-400">{details.count || ''} findings</span></>;
      case 'bulk_finding_rejected':
        return <>{nameEl} rejected <span className="text-gray-500 dark:text-gray-400">{details.count || ''} findings</span></>;
      case 'collaborator_added':
        return <>{nameEl} added <span className="text-gray-500 dark:text-gray-400">{details.name || 'a collaborator'}</span> ({details.access || 'view'})</>;
      case 'collaborator_updated':
        return <>{nameEl} changed access: {details.old_access} → {details.new_access}</>;
      case 'collaborator_removed':
        return <>{nameEl} removed <span className="text-gray-500 dark:text-gray-400">{details.name || 'a collaborator'}</span></>;
      case 'review_started':
        return <>{nameEl} started the review{details.name ? <> "<span className="font-medium text-gray-700 dark:text-gray-300">{details.name}</span>"</> : ''}{details.document_count ? <span className="text-gray-400"> &middot; {details.document_count} {details.document_count === 1 ? 'document' : 'documents'}</span> : ''}</>;
      case 'review_renamed':
        return <>{nameEl} renamed to "<span className="font-medium text-gray-700 dark:text-gray-300">{details.new_name || ''}</span>"</>;
      case 'review_deleted':
        return <>{nameEl} deleted the review</>;
      case 'status_change': {
        const statusLabels: Record<string, string> = {
          pending_sign_off: 'marked review as complete',
          finalized: 'finalized the review',
          completed: 'set review to in-review',
          processing: 'started processing',
          failed: 'review processing failed',
        };
        return <>{nameEl} {statusLabels[details.to] || `changed status to ${formatLabel(details.to || 'unknown')}`}</>;
      }      case 'sign_off':
        return <>{nameEl} signed off on the review</>;
      case 'review_finalized':
        return <>{nameEl} finalized the review</>;
      case 'review_reopened':
        return <>{nameEl} reopened the review</>;
      default:
        return <>{nameEl} {formatLabel(entry.action).toLowerCase()}</>;
    }
  };

  if (isLoading && auditLog.length === 0) {
    return (
      <div className="flex items-center justify-center h-32">
        <LogoSpinner size="small" />
      </div>
    );
  }

  if (auditLog.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-sm text-gray-400">
        <PiClockLight className="w-8 h-8 mb-2" />
        {t('No activity yet')}
      </div>
    );
  }

  return (
    <div className="py-2">
      {auditLog.map((entry, idx) => {
        const isClickable = !!(onFindingSelect && FINDING_ACTIONS.has(entry.action) && entry.entity_uuid);
        const isLast = idx === auditLog.length - 1;
        const dot = dotColors[entry.action] || 'bg-gray-300 dark:bg-gray-600';

        const entryContent = (
          <div className="flex items-start gap-3 relative">
            {/* Timeline dot + connector */}
            <div className="flex flex-col items-center flex-shrink-0 pt-1">
              <div className={clsx('w-2 h-2 rounded-full', dot)} />
              {!isLast && (
                <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 mt-1" style={{ minHeight: 20 }} />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pb-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-[13px] leading-snug text-gray-600 dark:text-gray-400">
                  {getActionDescription(entry)}
                </p>
                <span className="text-[11px] text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap pt-0.5">
                  {formatTime(entry.created_at)}
                </span>
              </div>
            </div>
          </div>
        );

        return isClickable ? (
          <button
            key={entry.id}
            type="button"
            onClick={() => {
              onFindingSelect!(entry.entity_uuid!);
              dispatch(showAuditLogDetailModal(mapToGenericEntry(entry)));
            }}
            className="w-full text-left px-4 cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-none"
          >
            {entryContent}
          </button>
        ) : (
          <div
            key={entry.id}
            className="px-4 cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50"
            onClick={() => dispatch(showAuditLogDetailModal(mapToGenericEntry(entry)))}
          >
            {entryContent}
          </div>
        );
      })}
    </div>
  );
};

export default ReviewAuditLog;
